import { useState, useRef, useCallback, useEffect } from 'react';
import type { ChatLogEntry, StreamStatus, UsageMetadata, TurnGroup, ThreadItemData } from '../components/chat/types';
import type {
  PendingHitlInterrupt,
  PendingApprovalInterrupt,
  HitlAnswerDraft,
  ApprovalDecision,
} from '../components/chat/hitl/types';
import {
  parseApprovalInterruptPayload,
} from '../components/chat/hitl/types';
import { createLogId } from '../components/chat/utils/createLogId';
import { mergeToolCalls } from '../components/chat/utils/logTransforms';
import {
  transformLogsToThreadItems,
  groupByTurn,
  createTurnGroupFromUserMessage,
  TASK_COLORS,
} from '../components/chat/utils/turnGrouping';
import { getAccessToken } from '../api/clients/tokenAccessor';
import { useHitlEnabled } from './useHitlEnabled';
import { chatHitlToolRegistry } from '../components/chat/hitl/registry';

interface Trace {
  kind: 'prompt' | 'request' | 'response' | 'tool_call' | 'tool_response';
  ts: string;
  prompt?: string;
  response?: string;
  tool?: string;
  args?: string;
  agent?: string;
  requestId?: string;
  id?: string;
  durationMs?: number;
  toolCallId?: string;
  turn?: number;
  // For 'request' kind - contains user messages
  messages?: Array<{ role: number; text: string }>;
}

interface StreamingEntry {
  id: string;
  requestId: string;
  agent?: string;
  content: string;
  turn?: number;
}

// Allowed trace kinds - same filtering for streaming as history loading
const ALLOWED_TRACE_KINDS = ['prompt', 'request', 'response', 'tool_call', 'tool_response'];

function convertTraceToLogEntry(trace: Trace, idx: number): ChatLogEntry {
  const baseEntry = {
    id: trace.id || trace.requestId || `history-${idx}`,
    ts: trace.ts,
    status: 'complete' as const,
    requestId: trace.requestId,
    turn: trace.turn,
  };

  switch (trace.kind) {
    case 'prompt':
      return {
        ...baseEntry,
        kind: 'request',
        agent: 'User',
        prompt: trace.prompt || '',
      };
    case 'request': {
      // Extract user message from messages array
      // messages[].role: 1 = user, 2 = assistant
      const userMessage = trace.messages?.find((m) => m.role === 1);
      let promptText = '';
      if (userMessage?.text) {
        try {
          // Text is JSON-stringified content array
          const content = JSON.parse(userMessage.text);
          promptText = content
            .filter((c: { type?: string; text?: string }) => c.type === 'text')
            .map((c: { text: string }) => c.text)
            .join('');
        } catch {
          promptText = userMessage.text;
        }
      }
      return {
        ...baseEntry,
        kind: 'request',
        agent: 'User',
        prompt: promptText,
      };
    }
    case 'response':
      return {
        ...baseEntry,
        kind: 'response',
        agent: trace.agent || 'Assistant',
        response: trace.response || '',
        durationMs: trace.durationMs,
      };
    case 'tool_call':
      return {
        ...baseEntry,
        kind: 'tool_call',
        tool: trace.tool,
        args: trace.args,
        toolCallId: trace.toolCallId,
        agent: trace.agent,
        durationMs: trace.durationMs,
      };
    case 'tool_response':
      return {
        ...baseEntry,
        kind: 'tool_response',
        toolCallId: trace.toolCallId,
        response: trace.response,
        agent: trace.agent,
        durationMs: trace.durationMs,
      };
    default:
      return {
        ...baseEntry,
        kind: 'response',
        response: '',
      };
  }
}

interface UseChatAgentProps {
  agentApiEndpoint: string;
  reportId: string;
  reportType: string;
  evidenceIds?: string[];
  handleSaveChanges?: () => Promise<void>;
  fetchReportData?: () => void;
  showError?: (message: string) => void;
}

export const useChatAgent = ({
  agentApiEndpoint,
  reportId,
  reportType,
  evidenceIds,
  handleSaveChanges,
  fetchReportData,
  showError,
}: UseChatAgentProps) => {
  const hitlEnabled = useHitlEnabled();
  const [logs, setLogs] = useState<ChatLogEntry[]>([]);
  const [turnGroups, setTurnGroups] = useState<TurnGroup[]>([]);
  const [threadId, setThreadId] = useState<string | null>(reportId);
  const [status, setStatus] = useState<StreamStatus>('idle');
  const [usage, setUsage] = useState<UsageMetadata | null>(null);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);

  // HITL state — custom tool interrupts (AskUserQuestion pattern)
  const [pendingHitlInterrupts, setPendingHitlInterrupts] = useState<PendingHitlInterrupt[]>([]);
  const [hitlDrafts, setHitlDrafts] = useState<Record<string, HitlAnswerDraft | undefined>>({});

  // HITL state — official middleware approval interrupts (approve/edit/reject)
  const [pendingApprovalInterrupts, setPendingApprovalInterrupts] = useState<PendingApprovalInterrupt[]>([]);
  const [approvalDecisions, setApprovalDecisions] = useState<Record<string, ApprovalDecision | undefined>>({});

  const [isSubmittingHitl, setIsSubmittingHitl] = useState(false);

  // Keep track of streaming responses by requestId (supports parallel agents)
  const streamingEntriesRef = useRef<Map<string, StreamingEntry>>(new Map());

  // Track task color index for consistent coloring across incremental updates
  const taskColorIndexRef = useRef<number>(0);

  // AbortController for cancelling in-flight requests
  const abortControllerRef = useRef<AbortController | null>(null);

  // Load thread history on mount
  useEffect(() => {
    const loadThreadHistory = async () => {
      if (!reportId) {
        setIsLoadingHistory(false);
        return;
      }

      try {
        let authHeaders: Record<string, string> = {};
        try {
          const token = await getAccessToken();
          authHeaders = { Authorization: `Bearer ${token}` };
        } catch {
          // Token not available — proceed without auth
        }

        const response = await fetch(`${agentApiEndpoint}/get-thread-history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...authHeaders },
          body: JSON.stringify({ threadId: reportId, limit: 1000 }),
        });

        if (!response.ok) {
          setIsLoadingHistory(false);
          return; // No history or error - start fresh
        }

        // Response is NDJSON (newline-delimited JSON) - parse each line
        const text = await response.text();
        const lines = text.split('\n').filter((line) => line.trim());
        const traces: Trace[] = [];

        for (const line of lines) {
          try {
            const parsed = JSON.parse(line);
            // Handle wrapped format: { type: 'trace', data: {...} }
            if (parsed.type === 'trace' && parsed.data) {
              traces.push(parsed.data);
            } else if (parsed.kind) {
              // Direct trace format
              traces.push(parsed);
            }
          } catch {
            // Skip malformed lines
          }
        }

        if (traces.length > 0) {
          const rawEntries: ChatLogEntry[] = traces
            .filter((t: Trace) =>
              t.kind === 'prompt' ||
              t.kind === 'request' ||
              t.kind === 'response' ||
              t.kind === 'tool_call' ||
              t.kind === 'tool_response'
            )
            .map((t: Trace, idx: number) => convertTraceToLogEntry(t, idx));

          // Merge tool_call and tool_response entries by toolCallId
          const entries = mergeToolCalls(rawEntries);

          // Mark all loaded entries as historical
          const historicalEntries = entries.map(entry => ({
            ...entry,
            isHistorical: true,
          }));
          setLogs(historicalEntries);

          // Compute turn groups once from historical data
          const items = transformLogsToThreadItems(historicalEntries);
          const groups = groupByTurn(items, false);
          setTurnGroups(groups);

          // Update task color index based on how many tasks were in history
          const taskCount = items.filter(item => item.type === 'task').length;
          taskColorIndexRef.current = taskCount;
        }
      } catch (error) {
        console.error('Failed to load thread history:', error);
        // Silently fail - user can still start a new conversation
      } finally {
        setIsLoadingHistory(false);
      }
    };

    loadThreadHistory();
  }, [reportId, agentApiEndpoint]);

  const sendMessage = useCallback(
    async (
      text: string,
      _audioBase64?: string,
      hitlResumeData?: { hitlResponses?: any[]; approvalDecisions?: any[] }
    ) => {
      if ((!text.trim() && !hitlResumeData) || status === 'streaming') return;

      // Calculate new turn number
      const newTurnNumber = turnGroups.length > 0
        ? turnGroups[turnGroups.length - 1].turn + 1
        : 1;

      let promptText = text;
      if (!text.trim() && hitlResumeData) {
        promptText = 'Provided answers';
        if (hitlResumeData.hitlResponses?.length) {
          promptText = hitlResumeData.hitlResponses.map(r => {
            const input = r.userInput;
            if (typeof input === 'object' && input !== null && 'selectedValues' in input) {
              return (input as any).skipped ? 'Skipped' : ((input as any).selectedValues || []).join(', ');
            }
            return 'Answer provided';
          }).join(' | ');
        } else if (hitlResumeData.approvalDecisions?.length) {
          const actions = hitlResumeData.approvalDecisions.flatMap(d => d.decisions.map((x: any) => x.action));
          promptText = actions.length > 0 ? `Action: ${actions.join(', ')}` : 'Action decided';
        }
      }

      // Add user request entry
      const userEntry: ChatLogEntry = {
        id: createLogId('user'),
        kind: 'request',
        agent: 'User',
        prompt: promptText,
        ts: new Date().toISOString(),
        status: 'complete',
        turn: newTurnNumber,
        isHistorical: false,
      };
      console.log('Sending message:', JSON.stringify(userEntry));

      setLogs((prev) => [...prev, userEntry]);

      // Create new turn group for user message
      const userItem: ThreadItemData = {
        id: userEntry.id,
        type: 'user',
        content: promptText,
        turn: newTurnNumber,
        isHistorical: false,
      };
      const newTurnGroup = createTurnGroupFromUserMessage(userItem, newTurnNumber);
      setTurnGroups((prev) => [...prev, newTurnGroup]);

      setStatus('streaming');

      try {
        // 1. Save current report state
        if (handleSaveChanges) {
          await handleSaveChanges();
        }

        // 2. Prepare payload - only send current message (backend tracks history via threadId)
        const payload: Record<string, unknown> = {
          configurable: {
            reportType,
            reportId,
            evidenceIds,
            presentPlanEnabled: hitlEnabled,
          },
          threadId: threadId,
          hitlToolRegistry: chatHitlToolRegistry,
        };
        
        if (text) {
          payload.messages = [{ role: 'user', content: [{ text, type: 'text' }] }];
        }
        
        if (hitlResumeData?.hitlResponses) {
          payload.hitlResponses = hitlResumeData.hitlResponses;
          setPendingHitlInterrupts([]);
          setHitlDrafts({});
        }
        
        if (hitlResumeData?.approvalDecisions) {
          payload.approvalDecisions = hitlResumeData.approvalDecisions;
          setPendingApprovalInterrupts([]);
          setApprovalDecisions({});
        }

        // 3. POST to agent API and stream response
        abortControllerRef.current = new AbortController();

        let invokeAuthHeaders: Record<string, string> = {};
        try {
          const token = await getAccessToken();
          invokeAuthHeaders = { Authorization: `Bearer ${token}` };
        } catch {
          // Token not available — proceed without auth
        }

        const response = await fetch(`${agentApiEndpoint}/invoke`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...invokeAuthHeaders,
          },
          body: JSON.stringify(payload),
          signal: abortControllerRef.current.signal,
        });

        if (!response.ok) {
          throw new Error(`API Error: ${response.status}`);
        }

        const reader = response.body?.getReader();
        if (!reader) throw new Error('No response body');

        const decoder = new TextDecoder();
        let buffer = '';

        // Reset streaming entries map
        streamingEntriesRef.current.clear();

        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          for (const line of lines) {
            if (!line.trim()) continue;

            try {
              const event = JSON.parse(line);

              switch (event.type) {
                case 'start':
                  if (event.data?.threadId) {
                    setThreadId(event.data.threadId);
                    // Update user entry with threadId and turn
                    setLogs((prev) =>
                      prev.map((l) =>
                        l.id === userEntry.id
                          ? { ...l, threadId: event.data.threadId, turn: event.data.turn }
                          : l
                      )
                    );
                  }
                  break;

                case 'token':
                  if (event.data?.content) {
                    const content = event.data.content;
                    const tokenAgent = event.data.agent;
                    const tokenRequestId = event.data.requestId;
                    const tokenTurn = event.data.turn;
                    const effectiveRequestId = tokenRequestId || 'default';
                    let textChunk = '';

                    // Handle tool_use tokens (streaming tool calls)
                    if (Array.isArray(content)) {
                      for (const item of content) {
                        if (item.type === 'tool_use' && item.id) {
                          // Create pending tool call entry if not exists
                          setLogs((prev) => {
                            const exists = prev.some(
                              (l) => l.toolCallId === item.id
                            );
                            if (exists) return prev;
                            return [
                              ...prev,
                              {
                                id: createLogId('token-tool'),
                                kind: 'tool_call',
                                tool: item.name || '',
                                args: item.input || '',
                                toolCallId: item.id,
                                ts: new Date().toISOString(),
                                status: 'pending',
                                inputStreaming: true,
                                requestId: tokenRequestId,
                                turn: tokenTurn,
                              } as ChatLogEntry,
                            ];
                          });
                        }

                        // Handle input_json_delta tokens (streaming tool input)
                        if (item.type === 'input_json_delta' && item.input) {
                          // Find the most recent pending tool call and append input
                          setLogs((prev) => {
                            const lastPendingToolIdx = [...prev].reverse().findIndex(
                              (l) => l.kind === 'tool_call' && l.status === 'pending'
                            );
                            if (lastPendingToolIdx === -1) return prev;
                            const actualIdx = prev.length - 1 - lastPendingToolIdx;

                            return prev.map((l, idx) => {
                              if (idx !== actualIdx) return l;
                              const currentArgs = l.args || '';
                              return {
                                ...l,
                                args: currentArgs + item.input,
                                inputStreaming: true,
                              };
                            });
                          });
                        }
                      }

                      // Extract text tokens
                      textChunk = content
                        .filter(
                          (c: { type?: string; text?: string }) =>
                            c.type === 'text' && typeof c.text === 'string'
                        )
                        .map((c: { text: string }) => c.text)
                        .join('');
                    } else if (typeof content === 'string') {
                      textChunk = content;
                    }

                    if (!textChunk) continue;

                    // Get or create streaming entry for this requestId
                    let streamEntry = streamingEntriesRef.current.get(effectiveRequestId);

                    if (!streamEntry) {
                      // Start new streaming response for this requestId
                      const newEntryId = createLogId('streaming');
                      const newEntry: ChatLogEntry = {
                        id: newEntryId,
                        kind: 'response',
                        response: '',
                        agent: tokenAgent,
                        requestId: effectiveRequestId !== 'default' ? effectiveRequestId : undefined,
                        ts: new Date().toISOString(),
                        status: 'streaming',
                        turn: tokenTurn,
                      };
                      setLogs((prev) => [...prev, newEntry]);

                      // Add as lastMessage in turn group
                      const newThreadItem: ThreadItemData = {
                        id: newEntryId,
                        type: 'ai-response',
                        status: 'active',
                        content: '',
                        turn: tokenTurn,
                        isHistorical: false,
                      };
                      setTurnGroups((prev) => {
                        const updated = [...prev];
                        const lastIdx = updated.length - 1;
                        if (lastIdx >= 0) {
                          const lastGroup = updated[lastIdx];
                          // Move current lastMessage to thinkingMessages if exists
                          const newThinking = lastGroup.lastMessage
                            ? [...lastGroup.thinkingMessages, lastGroup.lastMessage]
                            : lastGroup.thinkingMessages;
                          updated[lastIdx] = {
                            ...lastGroup,
                            thinkingMessages: newThinking,
                            lastMessage: newThreadItem,
                          };
                        }
                        return updated;
                      });

                      streamEntry = {
                        id: newEntry.id,
                        requestId: effectiveRequestId,
                        agent: tokenAgent,
                        content: '',
                        turn: tokenTurn,
                      };
                      streamingEntriesRef.current.set(effectiveRequestId, streamEntry);
                    }

                    // Accumulate content
                    streamEntry.content += textChunk;

                    const activeId = streamEntry.id;
                    const activeContent = streamEntry.content;

                    setLogs((prev) =>
                      prev.map((l) =>
                        l.id === activeId ? { ...l, response: activeContent } : l
                      )
                    );

                    // Update turn group's lastMessage content
                    setTurnGroups((prev) => {
                      const updated = [...prev];
                      const lastIdx = updated.length - 1;
                      if (lastIdx >= 0 && updated[lastIdx].lastMessage?.id === activeId) {
                        updated[lastIdx] = {
                          ...updated[lastIdx],
                          lastMessage: {
                            ...updated[lastIdx].lastMessage!,
                            content: activeContent,
                          },
                        };
                      }
                      return updated;
                    });
                  }
                  break;

                case 'trace': {
                  const data = event.data || {};
                  const { kind, tool, args, response: traceResponse, toolCallId, agent, durationMs, usage, requestId: traceRequestId, turn: traceTurn } = data;

                  // Handle HITL interrupt events (custom tools + official middleware)
                  if (kind === 'hitl_interrupt') {
                    const payload = data.payload || {};
                    setPendingHitlInterrupts((prev) => [
                      ...prev,
                      {
                        interruptId: data.id || `hitl-${Date.now()}`,
                        threadId: data.threadId,
                        namespace: data.namespace,
                        toolName: payload.toolName || 'unknown',
                        toolCallId: payload.toolCallId,
                        toolArgs: payload.toolArgs,
                        schema: payload.schema,
                        ts: data.ts || new Date().toISOString(),
                        turn: traceTurn,
                        source: 'stream' as const,
                      },
                    ]);
                    break;
                  }

                  if (kind === 'hitl_approval_interrupt') {
                    const payload = data.payload || {};
                    const approvalPayload = parseApprovalInterruptPayload(payload);
                    if (approvalPayload) {
                      setPendingApprovalInterrupts((prev) => [
                        ...prev,
                        {
                          interruptId: data.id || `approval-${Date.now()}`,
                          threadId: data.threadId,
                          namespace: data.namespace,
                          payload: approvalPayload,
                          ts: data.ts || new Date().toISOString(),
                          turn: traceTurn,
                          source: 'stream' as const,
                        },
                      ]);
                    }
                    break;
                  }

                  // Skip trace kinds not in the allowed list (same filtering as history)
                  if (!ALLOWED_TRACE_KINDS.includes(kind)) {
                    break;
                  }

                  const effectiveTraceRequestId = traceRequestId || 'default';

                  if (kind === 'tool_call') {
                    const toolEntryId = toolCallId || createLogId('trace-tool');
                    // Create pending tool call entry
                    setLogs((prev) => {
                      const exists = prev.some((l) => l.toolCallId === toolCallId);
                      if (exists) return prev;

                      const argsStr = typeof args === 'string' ? args : JSON.stringify(args);
                      return [
                        ...prev,
                        {
                          id: toolEntryId,
                          kind: 'tool_call',
                          tool,
                          args: argsStr,
                          toolCallId,
                          agent,
                          requestId: traceRequestId,
                          ts: new Date().toISOString(),
                          status: 'pending',
                          turn: traceTurn,
                        } as ChatLogEntry,
                      ];
                    });

                    // Add tool call to turn group's thinkingMessages
                    const isTask = tool?.toLowerCase() === 'task';
                    let title = 'Working...';
                    if (isTask) {
                      try {
                        const parsedArgs = typeof args === 'string' ? JSON.parse(args) : args || {};
                        title = (parsedArgs.description as string) || `${parsedArgs.subagent_type || 'Agent'}: Working...`;
                      } catch {
                        title = 'Task';
                      }
                    }

                    const toolItem: ThreadItemData = {
                      id: toolEntryId,
                      type: isTask ? 'task' : 'working',
                      status: 'active',
                      title,
                      taskColor: isTask ? TASK_COLORS[taskColorIndexRef.current % TASK_COLORS.length] : undefined,
                      turn: traceTurn,
                      isHistorical: false,
                    };

                    if (isTask) {
                      taskColorIndexRef.current++;
                    }

                    setTurnGroups((prev) => {
                      const updated = [...prev];
                      const lastIdx = updated.length - 1;
                      if (lastIdx >= 0) {
                        // Check if this tool call already exists in thinkingMessages
                        const exists = updated[lastIdx].thinkingMessages.some(m => m.id === toolEntryId);
                        if (!exists) {
                          updated[lastIdx] = {
                            ...updated[lastIdx],
                            thinkingMessages: [...updated[lastIdx].thinkingMessages, toolItem],
                          };
                        }
                      }
                      return updated;
                    });
                  } else if (kind === 'tool_response') {
                    // Merge response into existing tool call
                    setLogs((prev) => {
                      const existingIdx = prev.findIndex(
                        (l) => l.kind === 'tool_call' && l.toolCallId === toolCallId
                      );
                      if (existingIdx === -1) return prev;

                      const responseStr = typeof traceResponse === 'string'
                        ? traceResponse
                        : JSON.stringify(traceResponse);

                      return prev.map((l, idx) =>
                        idx === existingIdx
                          ? { ...l, response: responseStr, status: 'complete', durationMs }
                          : l
                      );
                    });

                    // Update tool status in turn group's thinkingMessages
                    setTurnGroups((prev) => {
                      const updated = [...prev];
                      const lastIdx = updated.length - 1;
                      if (lastIdx >= 0) {
                        const thinkingMessages = updated[lastIdx].thinkingMessages.map(m =>
                          m.id === toolCallId ? { ...m, status: 'completed' as const } : m
                        );
                        updated[lastIdx] = { ...updated[lastIdx], thinkingMessages };
                      }
                      return updated;
                    });
                  } else if (kind === 'response') {
                    // Handle response trace - display text and accumulate usage
                    if (traceResponse) {
                      // Finalize streaming entry by requestId
                      const streamEntry = streamingEntriesRef.current.get(effectiveTraceRequestId);
                      if (streamEntry) {
                        const streamingId = streamEntry.id;
                        setLogs((prev) =>
                          prev.map((l) =>
                            l.id === streamingId
                              ? { ...l, agent, status: 'complete', requestId: traceRequestId, turn: traceTurn }
                              : l
                          )
                        );

                        // Update turn group's lastMessage status to completed
                        setTurnGroups((prev) => {
                          const updated = [...prev];
                          const lastIdx = updated.length - 1;
                          if (lastIdx >= 0 && updated[lastIdx].lastMessage?.id === streamingId) {
                            updated[lastIdx] = {
                              ...updated[lastIdx],
                              lastMessage: {
                                ...updated[lastIdx].lastMessage!,
                                status: 'completed',
                              },
                            };
                          }
                          return updated;
                        });

                        streamingEntriesRef.current.delete(effectiveTraceRequestId);
                      } else {
                        // Create new response entry from trace
                        const newEntryId = data.id || createLogId('trace-response');
                        const newEntry: ChatLogEntry = {
                          id: newEntryId,
                          kind: 'response',
                          agent,
                          response: traceResponse,
                          requestId: traceRequestId,
                          ts: data.ts || new Date().toISOString(),
                          status: 'complete',
                          durationMs,
                          turn: traceTurn,
                        };
                        setLogs((prev) => [...prev, newEntry]);

                        // Add as lastMessage in turn group
                        const newThreadItem: ThreadItemData = {
                          id: newEntryId,
                          type: 'ai-response',
                          status: 'completed',
                          content: traceResponse,
                          turn: traceTurn,
                          isHistorical: false,
                        };
                        setTurnGroups((prev) => {
                          const updated = [...prev];
                          const lastIdx = updated.length - 1;
                          if (lastIdx >= 0) {
                            const lastGroup = updated[lastIdx];
                            // Move current lastMessage to thinkingMessages if exists
                            const newThinking = lastGroup.lastMessage
                              ? [...lastGroup.thinkingMessages, lastGroup.lastMessage]
                              : lastGroup.thinkingMessages;
                            updated[lastIdx] = {
                              ...lastGroup,
                              thinkingMessages: newThinking,
                              lastMessage: newThreadItem,
                            };
                          }
                          return updated;
                        });
                      }
                    }

                    // Accumulate usage metadata
                    if (usage) {
                      setUsage((prev) => ({
                        input_tokens: (prev?.input_tokens || 0) + (usage.input_tokens || 0),
                        output_tokens: (prev?.output_tokens || 0) + (usage.output_tokens || 0),
                        total_tokens: (prev?.total_tokens || 0) + (usage.total_tokens || 0),
                      }));
                    }
                  }
                  break;
                }

                case 'complete':
                  abortControllerRef.current = null;
                  // Finalize ALL remaining streaming entries
                  const remainingEntries = Array.from(streamingEntriesRef.current.values());
                  if (remainingEntries.length > 0) {
                    const idsToComplete = remainingEntries.map(e => e.id);
                    setLogs((prev) =>
                      prev.map((l) =>
                        idsToComplete.includes(l.id) ? { ...l, status: 'complete' } : l
                      )
                    );
                    streamingEntriesRef.current.clear();
                  }

                  // Mark turn group as not streaming
                  setTurnGroups((prev) => {
                    const updated = [...prev];
                    const lastIdx = updated.length - 1;
                    if (lastIdx >= 0) {
                      updated[lastIdx] = { ...updated[lastIdx], isStreaming: false };
                      // Also mark lastMessage as completed if still active
                      if (updated[lastIdx].lastMessage?.status === 'active') {
                        updated[lastIdx] = {
                          ...updated[lastIdx],
                          lastMessage: {
                            ...updated[lastIdx].lastMessage!,
                            status: 'completed',
                          },
                        };
                      }
                    }
                    return updated;
                  });

                  setStatus('done');
                  // Reload report data
                  if (fetchReportData) {
                    await fetchReportData();
                  }
                  break;

                case 'error':
                  // Mark all streaming entries as error
                  const errorEntries = Array.from(streamingEntriesRef.current.values());
                  if (errorEntries.length > 0) {
                    const idsToError = errorEntries.map(e => e.id);
                    setLogs((prev) =>
                      prev.map((l) =>
                        idsToError.includes(l.id)
                          ? { ...l, status: 'error', response: l.response || 'Error occurred' }
                          : l
                      )
                    );
                    streamingEntriesRef.current.clear();
                  }
                  if (showError)
                    showError(event.data?.message || 'Unknown error');
                  setStatus('error');
                  break;
              }
            } catch (e) {
              console.error('Error parsing stream line:', e);
            }
          }
        }

        // Stream ended - ensure status is set to done
        setStatus((currentStatus) =>
          currentStatus === 'streaming' ? 'done' : currentStatus
        );
      } catch (error: unknown) {
        // Check if this was an intentional abort
        if (error instanceof Error && error.name === 'AbortError') {
          console.log('Stream cancelled by user');
          setStatus('idle');
          return;
        }

        console.error('Chat error:', error);
        if (showError)
          showError(error instanceof Error ? error.message : 'Unknown error');

        // Update all streaming entries to error state
        const catchErrorEntries = Array.from(streamingEntriesRef.current.values());
        if (catchErrorEntries.length > 0) {
          const idsToError = catchErrorEntries.map(e => e.id);
          setLogs((prev) =>
            prev.map((l) =>
              idsToError.includes(l.id)
                ? {
                    ...l,
                    status: 'error',
                    response: l.response || 'Error occurred.',
                  }
                : l
            )
          );
          streamingEntriesRef.current.clear();
        } else {
          // Fallback error entry if no streaming entries exist
          const errorEntry: ChatLogEntry = {
            id: createLogId('error'),
            kind: 'error',
            response: 'Failed to send message. Please try again.',
            ts: new Date().toISOString(),
            status: 'error',
          };
          setLogs((prev) => [...prev, errorEntry]);
        }
        setStatus('error');
      } finally {
        setStatus((currentStatus) =>
          currentStatus === 'streaming' ? 'done' : currentStatus
        );
        streamingEntriesRef.current.clear();
      }
    },
    [
      agentApiEndpoint,
      turnGroups,
      threadId,
      status,
      handleSaveChanges,
      fetchReportData,
      showError,
      reportType,
      reportId,
      evidenceIds,
      hitlEnabled,
    ]
  );

  const cancelStream = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;

      // Mark ALL streaming entries as cancelled
      const cancelEntries = Array.from(streamingEntriesRef.current.values());
      if (cancelEntries.length > 0) {
        const idsToCancel = cancelEntries.map(e => e.id);
        setLogs((prev) =>
          prev.map((l) =>
            idsToCancel.includes(l.id)
              ? { ...l, status: 'error', response: l.response || 'Cancelled' }
              : l
          )
        );
        streamingEntriesRef.current.clear();
      }

      setStatus('idle');
    }
  }, []);

  // --- HITL resume submission ---

  const submitHitlResume = useCallback(async (
    hitlResponses: Array<{ interruptId: string; userInput: unknown }>,
    approvalDecisionPayloads?: Array<{ interruptId: string; decisions: ApprovalDecision[] }>,
  ) => {
    setIsSubmittingHitl(true);
    try {
      await sendMessage('', undefined, { hitlResponses, approvalDecisions: approvalDecisionPayloads });
    } finally {
      setIsSubmittingHitl(false);
    }
  }, [sendMessage]);

  const handleHitlDraftChange = useCallback((interruptId: string, draft: HitlAnswerDraft) => {
    setHitlDrafts((prev) => ({ ...prev, [interruptId]: draft }));
  }, []);

  const handleApprovalDecisionChange = useCallback((interruptId: string, decision: ApprovalDecision) => {
    setApprovalDecisions((prev) => ({ ...prev, [interruptId]: decision }));
  }, []);

  const hasActiveInterrupts = pendingHitlInterrupts.length > 0 || pendingApprovalInterrupts.length > 0;

  return {
    logs,
    turnGroups,
    sendMessage,
    cancelStream,
    isStreaming: status === 'streaming',
    status,
    usage,
    threadId,
    isLoadingHistory,
    // HITL — custom tool interrupts
    pendingHitlInterrupts,
    hitlDrafts,
    handleHitlDraftChange,
    // HITL — approval interrupts
    pendingApprovalInterrupts,
    approvalDecisions,
    handleApprovalDecisionChange,
    // HITL — shared
    submitHitlResume,
    isSubmittingHitl,
    hasActiveInterrupts,
  };
};

// Re-export types for convenience
export type { ChatLogEntry, StreamStatus, UsageMetadata };
